import { Button, Col, Row, Select } from 'antd';
import { DefaultOptionType, SelectProps } from 'antd/es/select';
import { defaultGradePreset } from 'constants/GlobalConfig';

const { Option } = Select;

/**
 * 学年コードのフィルタリング条件を定義
 * 各プロパティは学年コードを引数に取り、条件に合致するかboolean値を返す関数
 */
const GRADE_FILTERS = {
    /** 全学年を選択（常にtrueを返す） */
    ALL: () => true,
    /** 小学校（コードが'P'で始まる） */
    PRIMARY: (code: string) => code.startsWith('P'),
    /** 中学校（コードが'J'で始まる） */
    JUNIOR_HIGH: (code: string) => code.startsWith('J'),
    /** 学年制高等学校（コードが'H'で始まる） */
    SENIOR_HIGH: (code: string) => code.startsWith('H'),
    /** 単位制高等学校（コードが数字のみ） */
    CREDIT_BASED: (code: string) => /^\d+$/.test(code),
} as const;

/**
 * 一括選択ボタンのラベル定義
 * メンテナンス性向上のため、表示テキストを定数化
 */
const BUTTON_LABELS = {
    ALL: '全選択',
    PRIMARY: '小学校',
    JUNIOR_HIGH: '中学校',
    SENIOR_HIGH: '学年制高等学校',
    CREDIT_BASED: '単位制高等学校',
} as const;

/**
 * 一括選択ボタンの設定配列
 * ループ処理で使用し、コードの重複を防ぐ
 */
const QUICK_SELECT_BUTTONS = [
    { key: 'ALL', label: BUTTON_LABELS.ALL, filter: GRADE_FILTERS.ALL },
    {
        key: 'PRIMARY',
        label: BUTTON_LABELS.PRIMARY,
        filter: GRADE_FILTERS.PRIMARY,
    },
    {
        key: 'JUNIOR_HIGH',
        label: BUTTON_LABELS.JUNIOR_HIGH,
        filter: GRADE_FILTERS.JUNIOR_HIGH,
    },
    {
        key: 'SENIOR_HIGH',
        label: BUTTON_LABELS.SENIOR_HIGH,
        filter: GRADE_FILTERS.SENIOR_HIGH,
    },
    {
        key: 'CREDIT_BASED',
        label: BUTTON_LABELS.CREDIT_BASED,
        filter: GRADE_FILTERS.CREDIT_BASED,
    },
] as const;

/**
 * 対象学年選択コンポーネント
 * 複数の学年を選択可能なセレクトボックスと、
 * 学校種別ごとに一括選択するためのボタンを提供する
 */
interface TargetGradesSelectProps extends Omit<SelectProps<string[]>, 'mode' | 'children'> {
    /**
     * 学年選択変更時のコールバック関数
     */
    onChange?: (
        value: string[],
        option: DefaultOptionType | DefaultOptionType[],
    ) => void;
}

export const TargetGradesSelect = ({ onChange, disabled = false, ...rest }: TargetGradesSelectProps) => {
    /**
     * 指定されたフィルタ条件に基づいて学年を一括選択する
     * @param filterFn - 学年コードをフィルタリングする条件関数
     */
    const selectByFilter = (filterFn: (code: string) => boolean): void => {
        const items = defaultGradePreset.filter((item) => filterFn(item.code));
        const codes = items.map((item) => item.code);
        const options = items.map((item) => ({
            label: item.name,
            value: item.code,
        }));
        onChange?.(codes, options);
    };

    return (
        <>
            <Select
                {...rest}
                onChange={onChange}
                mode='multiple'
                disabled={disabled}
            >
                {defaultGradePreset.map((value) => (
                    <Option
                        key={value.code}
                        value={value.code}
                    >
                        {value.name}
                    </Option>
                ))}
            </Select>
            <Row gutter={[16, 8]}>
                {QUICK_SELECT_BUTTONS.map((button) => (
                    <Col key={button.key}>
                        <Button
                            type='link'
                            onClick={() => selectByFilter(button.filter)}
                            style={{ fontSize: '12px', padding: 0 }}
                            disabled={disabled}
                        >
                            {button.label}
                        </Button>
                    </Col>
                ))}
            </Row>
        </>
    );
};
